import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Outlet, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { I18nProvider } from "@/lib/i18n";
import { AuthProvider, useAuth } from "@/lib/auth";
import { CartProvider } from "@/lib/cart";
import { api } from "@/lib/api";
import Header from "@/components/store/Header";
import Footer from "@/components/store/Footer";
import CartDrawer from "@/components/store/CartDrawer";
import Home from "@/pages/Home";
import Products from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import Checkout from "@/pages/Checkout";
import { PaymentReturn } from "@/pages/Payment";
import { Privacy, Terms, ShippingPolicy } from "@/pages/Legal";
import { ChatWidget } from "@/components/store/ChatWidget";
import Track from "@/pages/Track";
import { Login, Register, ForgotPassword, ResetPassword } from "@/pages/Auth";
import Account from "@/pages/Account";
import Policy from "@/pages/Policy";
import Support from "@/pages/Support";
import AdminApp from "@/pages/admin/AdminApp";

function StoreLayout() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return (<><Header /><main className="min-h-[60vh]"><Outlet /></main><Footer /><CartDrawer /><ChatWidget /></>);
}

function AuthCallback() {
  const done = useRef(false);
  const { hash } = useLocation();
  const nav = useNavigate();
  const { setUser } = useAuth();
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(hash.slice(1)).get("session_id");
    api.post("/auth/google/session", { session_id: sid })
      .then(({ data }) => { setUser(data); window.history.replaceState(null, "", window.location.pathname); nav("/account", { replace: true }); })
      .catch(() => nav("/login", { replace: true }));
  }, [hash, nav, setUser]);
  return <div className="min-h-screen" />;
}

export function RequireAuth({ children, staff }) {
  const { user, isStaff } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="min-h-[50vh]" />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname)}`} replace />;
  if (staff && !isStaff) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/admin/*" element={<RequireAuth staff><AdminApp /></RequireAuth>} />
      <Route element={<StoreLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/products" element={<Products />} />
        <Route path="/p/:slug" element={<ProductDetail />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/payment/return" element={<PaymentReturn />} />
        <Route path="/track" element={<Track />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/account/*" element={<RequireAuth><Account /></RequireAuth>} />
        <Route path="/policy" element={<Policy />} />
        <Route path="/refund-policy" element={<Policy />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/shipping-policy" element={<ShippingPolicy />} />
        <Route path="/support" element={<Support />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <CartProvider>
          <BrowserRouter><AppRouter /></BrowserRouter>
          <Toaster position="top-center" richColors />
        </CartProvider>
      </AuthProvider>
    </I18nProvider>
  );
}
